import '@/styles/globals.css';
import '@copilotkit/react-ui/styles.css';
import { plusJakartaSans } from '@/lib/fonts';
import type { AppProps } from 'next/app';
import { initAuth } from '@/lib/auth.util';
import { SuperTokensWrapper } from 'supertokens-auth-react';
import { SessionAuth } from 'supertokens-auth-react/recipe/session';
import { ApolloProvider } from '@apollo/client';
import { OrganizationProvider } from '@/contexts/organization-context';
import { RootLayout } from '@/components/layouts/root-layout';
import createApolloClient from '@/lib/apollo-client';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { env } from 'next-runtime-env';
import { useRouter } from 'next/router';
import { ReactElement, ReactNode, useEffect } from 'react';
import Head from 'next/head';
import i18n from '@/i18n/config';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorOccurredPage } from '@/components/compound-components/common/error/error-page';
import { initializeCoralogixRUM, setRUMLabels, useRUMUser } from '@/lib/rum';
import { UserProvider } from '@/contexts/user-context';
import { TooltipProvider } from '@/components/shadcn/tooltip';
import { GrantProvider } from '@/contexts/grant-context';
import { I18nextProvider } from 'react-i18next';
import { useIntercom } from '@/lib/intercom';
import { NextPage } from 'next';
import { SettingsProvider } from '@/contexts/settings-context';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';

if (env('ENVIRONMENT') === 'local') {
  // Adds messages only in a local environment
  loadDevMessages();
  loadErrorMessages();
}

if (typeof window !== 'undefined') {
  initAuth();

  posthog.init(env('POSTHOG_KEY') || '', {
    api_host: env('POSTHOG_HOST') || 'https://ph.vee.com',
    session_recording: {
      maskAllInputs: false,
    },
    persistence: 'cookie',
    loaded: (posthog) => {
      if (env('ENVIRONMENT') === 'dev') posthog.debug();
      setRUMLabels({
        posthogSessionId: posthog.get_session_id(),
        posthogDistinctId: posthog.get_distinct_id(),
      });
    },
  });
}

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

export default function GrantApp({ Component, pageProps }: AppPropsWithLayout) {
  const getLayout = Component.getLayout ?? ((page) => page);

  const router = useRouter();

  useIntercom();
  useRUMUser();

  useEffect(() => {
    if (env('ENVIRONMENT') !== 'local') {
      initializeCoralogixRUM();
    }
    // Track page views
    const handleRouteChange = () => posthog?.capture('$pageview');
    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apolloClient = createApolloClient();

  return (
    <>
      <Head>
        <title>Vee</title>
      </Head>

      <style jsx global>{`
        :root {
          --font-plus-jakarta-sans: ${plusJakartaSans.style.fontFamily};
        }
      `}</style>
      <ErrorBoundary FallbackComponent={ErrorOccurredPage}>
        <SuperTokensWrapper>
          <PostHogProvider client={posthog}>
            <I18nextProvider i18n={i18n}>
              <SessionAuth>
                <ApolloProvider client={apolloClient}>
                  <UserProvider>
                    <SettingsProvider>
                      <OrganizationProvider>
                        <GrantProvider>
                          <TooltipProvider>
                            <div
                              className={`min-h-screen bg-background font-sans antialiased`}
                            >
                              <RootLayout>
                                {getLayout(<Component {...pageProps} />)}
                              </RootLayout>
                            </div>
                          </TooltipProvider>
                        </GrantProvider>
                      </OrganizationProvider>
                    </SettingsProvider>
                  </UserProvider>
                </ApolloProvider>
              </SessionAuth>
            </I18nextProvider>
          </PostHogProvider>
        </SuperTokensWrapper>
      </ErrorBoundary>
    </>
  );
}
